/**
 * Lightweight linkify utility
 * Replaces linkifyjs with a focused implementation for URL and email detection
 */

/**
 * Escape HTML special characters to prevent XSS
 * Note: We don't escape forward slashes as they're valid in URLs
 */
export function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Convert URLs in text to clickable links
 * Handles URLs with and without protocols
 */
export function linkifyText(text: string, options: { target?: string; rel?: string } = {}): string {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const { target = '_blank', rel = 'noopener noreferrer' } = options;
  
  // Pattern for URLs with protocol (http://, https://, ftp://)
  // Includes support for localhost and IP addresses
  const protocolUrlPattern = /(?:https?|ftp):\/\/(?:www\.)?(?:localhost|(?:[0-9]{1,3}\.){3}[0-9]{1,3}|[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})\b(?::[0-9]{1,5})?(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*[-a-zA-Z0-9@%_\+~#\/=])?/gi;
  
  // Pattern for URLs without protocol (must have www. or common TLDs)
  const nakedUrlPattern = /(?:^|[\s])(?:www\.[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*[-a-zA-Z0-9@%_\+~#\/=])?)|(?:^|[\s])(?:[-a-zA-Z0-9@:%._\+~#=]{1,256}\.(?:com|org|net|edu|gov|mil|int|io|co|uk|de|fr|jp|au|us|ru|ch|it|nl|se|no|es|mil|info|biz|name|me|tv|cc|ws|ca|cn|in|br|mx|ar|za|kr|sg|hk|tw|my|th|vn|id|ph|nz|eg|ng|ke|il|ae|sa|qa|kw|om|jo|lb|sy|iq|ir|ly|ma|tn|dz|sd|et|ug|tz|gh|ci|sn|zm|zw|bw|mz|na|za|ao|cm|ml|ne|bf|tg|bj|mg|mu|sc|rw|so|dj|km|yt|re|mq|gp|gf|nc|pf|vu|fj|to|ws|as|mp|gu|pr|vi|do|ht|jm|tt|bb|ag|dm|gd|kn|lc|vc|bs|ky|vg|bm|aw|ai|ms|tc|sx|cw|bq|aw|app|dev|tech|online|site|shop|store|blog|cloud|digital|services|solutions|media|news|today|world|plus|pro|web|network|systems|global|agency|studio|design|consulting|marketing|finance|health|realty|properties|rentals|legal|dental|medical|clinic|surgery|attorney|lawyer|accountant|insurance|bank|credit|loan|mortgage|trading|forex|crypto|bitcoin|travel|tours|hotel|flights|vacations|holiday|restaurant|food|delivery|pizza|coffee|bar|pub|club|gym|fitness|yoga|spa|salon|beauty|fashion|style|shoes|jewelry|watch|accessories|furniture|garden|pet|vet|baby|kids|toys|games|sport|sports|soccer|football|basketball|tennis|golf|cricket|rugby|hockey|baseball|racing|cycling|running|swimming|fishing|hunting|camping|hiking|outdoor|adventure|photography|music|movie|film|video|book|art|gallery|museum|theater|dance|events|tickets|shows|concerts|festival|party|wedding|education|university|college|school|academy|institute|course|class|tutorial|training|certification|exam|test|quiz|homework|assignment|project|research|thesis|dissertation|paper|essay|article|journal|magazine|newspaper|press|publishing|print|library|archive|database|software|hardware|computer|laptop|desktop|mobile|phone|tablet|ipad|iphone|android|windows|mac|linux|server|hosting|domain|website|email|internet|wifi|network|security|antivirus|firewall|vpn|proxy|download|upload|share|stream|broadcast|podcast|radio|television|cable|satellite|telecom|communication|chat|message|forum|social|facebook|twitter|instagram|linkedin|youtube|tiktok|snapchat|pinterest|reddit|whatsapp|telegram|discord|slack|zoom|skype|teams|meet|dating|match|love|relationship|family|friends|community|group|team|company|corporation|business|startup|enterprise|organization|association|foundation|charity|nonprofit|government|military|police|fire|emergency|hospital|pharmacy|laboratory|science|research|technology|engineering|construction|manufacturing|production|factory|warehouse|logistics|shipping|delivery|transport|freight|cargo|import|export|wholesale|retail|ecommerce|marketplace|auction|deals|coupons|discount|sale|clearance|outlet|mall|shopping|center|plaza|square|market|bazaar|fair|expo|convention|conference|summit|meeting|workshop|seminar|webinar|presentation|lecture|speech|talk|interview|discussion|debate|panel|roundtable|forum|symposium|congress|parliament|senate|assembly|council|committee|commission|board|agency|department|ministry|office|bureau|division|section|unit|branch|headquarters|regional|local|national|international|worldwide|universal)\b)(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*[-a-zA-Z0-9@%_\+~#\/=])?/gi;
  
  // Collect all URL matches with their positions
  const matches: Array<{ start: number; end: number; url: string; href: string }> = [];
  
  // Find all URLs with protocols
  let match;
  protocolUrlPattern.lastIndex = 0;
  while ((match = protocolUrlPattern.exec(text)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      url: match[0],
      href: match[0]
    });
  }
  
  // Find all URLs without protocols
  nakedUrlPattern.lastIndex = 0;
  while ((match = nakedUrlPattern.exec(text)) !== null) {
    const fullMatch = match[0];
    const url = fullMatch.trim();
    const leadingSpace = fullMatch.startsWith(' ') ? ' ' : '';
    const actualStart = match.index + leadingSpace.length;
    
    // Check if this is part of an email address (has @ before it)
    // Look for @ within the 30 characters before the domain
    const lookbackLength = 30;
    const contextBefore = text.substring(Math.max(0, actualStart - lookbackLength), actualStart);
    // Check if there's an @ followed by non-space characters leading up to our match
    const emailPattern = /@[^\s]*$/;
    const isEmail = emailPattern.test(contextBefore);
    
    // Check if this overlaps with any protocol URL
    const overlaps = matches.some(m => 
      (actualStart >= m.start && actualStart < m.end) ||
      (actualStart + url.length > m.start && actualStart + url.length <= m.end)
    );
    
    if (!overlaps && !isEmail) {
      // Add http:// to naked URLs
      const href = url.startsWith('www.') || /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(url) 
        ? `http://${url}` 
        : url;
      
      matches.push({
        start: actualStart,
        end: actualStart + url.length,
        url,
        href
      });
    }
  }
  
  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);
  
  // Build the result string with escaped HTML and linked URLs
  let result = '';
  let lastIndex = 0;
  
  for (const urlMatch of matches) {
    // Add escaped text before the URL
    if (urlMatch.start > lastIndex) {
      result += escapeHtml(text.substring(lastIndex, urlMatch.start));
    }

    // Add the linked URL (escape the display text but not the href)
    const targetAttr = target ? ` target="${target}"` : '';
    const relAttr = rel ? ` rel="${rel}"` : '';
    result += `<a href="${escapeHtml(urlMatch.href)}"${targetAttr}${relAttr}>${escapeHtml(urlMatch.url)}</a>`;

    lastIndex = urlMatch.end;
  }
  
  // Add any remaining escaped text
  if (lastIndex < text.length) {
    result += escapeHtml(text.substring(lastIndex));
  }
  
  return result;
}

/**
 * Convert email addresses in text to mailto links
 */
export function linkifyEmails(text: string): string {
  if (!text || typeof text !== 'string') {
    return text;
  }
  
  // Email pattern - fairly permissive but practical
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  
  // Collect all email matches with their positions
  const matches: Array<{ start: number; end: number; email: string }> = [];
  
  let match;
  emailPattern.lastIndex = 0;
  while ((match = emailPattern.exec(text)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      email: match[0]
    });
  }
  
  // Build the result string with escaped HTML and linked emails
  let result = '';
  let lastIndex = 0;
  
  for (const emailMatch of matches) {
    // Add escaped text before the email
    if (emailMatch.start > lastIndex) {
      result += escapeHtml(text.substring(lastIndex, emailMatch.start));
    }

    // Add the linked email
    result += `<a href="mailto:${escapeHtml(emailMatch.email)}">${escapeHtml(emailMatch.email)}</a>`;

    lastIndex = emailMatch.end;
  }
  
  // Add any remaining escaped text
  if (lastIndex < text.length) {
    result += escapeHtml(text.substring(lastIndex));
  }
  
  return result;
}

/**
 * Linkify both URLs and emails in text
 */
export function linkifyAll(text: string, options: { target?: string; rel?: string } = {}): string {
  if (!text || typeof text !== 'string') {
    return text;
  }
  
  // First, identify all emails and URLs with their positions
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi;
  const emails: Array<{ start: number; end: number; text: string }> = [];
  
  let match;
  emailPattern.lastIndex = 0;
  while ((match = emailPattern.exec(text)) !== null) {
    emails.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[0]
    });
  }
  
  // Now process URLs, but skip any that overlap with emails
  const protocolUrlPattern = /(?:https?|ftp):\/\/(?:www\.)?(?:localhost|(?:[0-9]{1,3}\.){3}[0-9]{1,3}|[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})\b(?::[0-9]{1,5})?(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*[-a-zA-Z0-9@%_\+~#\/=])?/gi;
  const nakedUrlPattern = /(?:^|[\s])(?:www\.[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*[-a-zA-Z0-9@%_\+~#\/=])?)|(?:^|[\s])(?:[-a-zA-Z0-9@:%._\+~#=]{1,256}\.(?:com|org|net|edu|gov|mil|int|io|co|uk|de|fr|jp|au|us|ru|ch|it|nl|se|no|es|mil|info|biz|name|me|tv|cc|ws|ca|cn|in|br|mx|ar|za|kr|sg|hk|tw|my|th|vn|id|ph|nz|eg|ng|ke|il|ae|sa|qa|kw|om|jo|lb|sy|iq|ir|ly|ma|tn|dz|sd|et|ug|tz|gh|ci|sn|zm|zw|bw|mz|na|za|ao|cm|ml|ne|bf|tg|bj|mg|mu|sc|rw|so|dj|km|yt|re|mq|gp|gf|nc|pf|vu|fj|to|ws|as|mp|gu|pr|vi|do|ht|jm|tt|bb|ag|dm|gd|kn|lc|vc|bs|ky|vg|bm|aw|ai|ms|tc|sx|cw|bq|aw|app|dev|tech|online|site|shop|store|blog|cloud|digital|services|solutions|media|news|today|world|plus|pro|web|network|systems|global|agency|studio|design|consulting|marketing|finance|health|realty|properties|rentals|legal|dental|medical|clinic|surgery|attorney|lawyer|accountant|insurance|bank|credit|loan|mortgage|trading|forex|crypto|bitcoin|travel|tours|hotel|flights|vacations|holiday|restaurant|food|delivery|pizza|coffee|bar|pub|club|gym|fitness|yoga|spa|salon|beauty|fashion|style|shoes|jewelry|watch|accessories|furniture|garden|pet|vet|baby|kids|toys|games|sport|sports|soccer|football|basketball|tennis|golf|cricket|rugby|hockey|baseball|racing|cycling|running|swimming|fishing|hunting|camping|hiking|outdoor|adventure|photography|music|movie|film|video|book|art|gallery|museum|theater|dance|events|tickets|shows|concerts|festival|party|wedding|education|university|college|school|academy|institute|course|class|tutorial|training|certification|exam|test|quiz|homework|assignment|project|research|thesis|dissertation|paper|essay|article|journal|magazine|newspaper|press|publishing|print|library|archive|database|software|hardware|computer|laptop|desktop|mobile|phone|tablet|ipad|iphone|android|windows|mac|linux|server|hosting|domain|website|email|internet|wifi|network|security|antivirus|firewall|vpn|proxy|download|upload|share|stream|broadcast|podcast|radio|television|cable|satellite|telecom|communication|chat|message|forum|social|facebook|twitter|instagram|linkedin|youtube|tiktok|snapchat|pinterest|reddit|whatsapp|telegram|discord|slack|zoom|skype|teams|meet|dating|match|love|relationship|family|friends|community|group|team|company|corporation|business|startup|enterprise|organization|association|foundation|charity|nonprofit|government|military|police|fire|emergency|hospital|pharmacy|laboratory|science|research|technology|engineering|construction|manufacturing|production|factory|warehouse|logistics|shipping|delivery|transport|freight|cargo|import|export|wholesale|retail|ecommerce|marketplace|auction|deals|coupons|discount|sale|clearance|outlet|mall|shopping|center|plaza|square|market|bazaar|fair|expo|convention|conference|summit|meeting|workshop|seminar|webinar|presentation|lecture|speech|talk|interview|discussion|debate|panel|roundtable|forum|symposium|congress|parliament|senate|assembly|council|committee|commission|board|agency|department|ministry|office|bureau|division|section|unit|branch|headquarters|regional|local|national|international|worldwide|universal)\b)(?:[-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*[-a-zA-Z0-9@%_\+~#\/=])?/gi;
  
  const urls: Array<{ start: number; end: number; url: string; href: string }> = [];
  
  // Find protocol URLs
  protocolUrlPattern.lastIndex = 0;
  while ((match = protocolUrlPattern.exec(text)) !== null) {
    // Check if this overlaps with any email
    const overlapsEmail = emails.some(e => 
      (match.index >= e.start && match.index < e.end) ||
      (match.index + match[0].length > e.start && match.index + match[0].length <= e.end)
    );
    
    if (!overlapsEmail) {
      urls.push({
        start: match.index,
        end: match.index + match[0].length,
        url: match[0],
        href: match[0]
      });
    }
  }
  
  // Find naked URLs
  nakedUrlPattern.lastIndex = 0;
  while ((match = nakedUrlPattern.exec(text)) !== null) {
    const fullMatch = match[0];
    const url = fullMatch.trim();
    const leadingSpace = fullMatch.startsWith(' ') ? ' ' : '';
    const actualStart = match.index + leadingSpace.length;
    
    // Check if this overlaps with any email or existing URL
    const overlapsEmail = emails.some(e => 
      (actualStart >= e.start && actualStart < e.end) ||
      (actualStart + url.length > e.start && actualStart + url.length <= e.end)
    );
    
    const overlapsUrl = urls.some(u => 
      (actualStart >= u.start && actualStart < u.end) ||
      (actualStart + url.length > u.start && actualStart + url.length <= u.end)
    );
    
    if (!overlapsEmail && !overlapsUrl) {
      const href = url.startsWith('www.') || /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(url) 
        ? `http://${url}` 
        : url;
      
      urls.push({
        start: actualStart,
        end: actualStart + url.length,
        url,
        href
      });
    }
  }
  
  // Combine emails and URLs, sort by position
  const allMatches = [
    ...emails.map(e => ({ ...e, type: 'email' as const })),
    ...urls.map(u => ({ ...u, type: 'url' as const }))
  ].sort((a, b) => a.start - b.start);
  
  // Build the result string
  let result = '';
  let lastIndex = 0;
  const { target = '_blank', rel = 'noopener noreferrer' } = options;

  for (const linkMatch of allMatches) {
    // Add escaped text before the match
    if (linkMatch.start > lastIndex) {
      result += escapeHtml(text.substring(lastIndex, linkMatch.start));
    }
    
    // Add the linked content
    if (linkMatch.type === 'email') {
      result += `<a href="mailto:${escapeHtml(linkMatch.text)}">${escapeHtml(linkMatch.text)}</a>`;
    } else {
      const targetAttr = target ? ` target="${target}"` : '';
      const relAttr = rel ? ` rel="${rel}"` : '';
      result += `<a href="${escapeHtml(linkMatch.href)}"${targetAttr}${relAttr}>${escapeHtml(linkMatch.url)}</a>`;
    }

    lastIndex = linkMatch.end;
  }
  
  // Add any remaining escaped text
  if (lastIndex < text.length) {
    result += escapeHtml(text.substring(lastIndex));
  }
  
  return result;
}

/**
 * Linkify HTML - main function to replace linkifyjs/html
 * This is the drop-in replacement for linkifyHtml
 */
export default function linkifyHtml(text: string, options: any = {}): string {
  const target = options.target || options.defaultTarget;
  const rel = options.rel || options.defaultRel;
  
  return linkifyAll(text, { target, rel });
}